import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Drawer from '@mui/material/Drawer';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';

import Logo from '../assets/logo.png';
import EmergencyBanner from './EmergencyBanner';

const Header = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [adhdAnchorEl, setAdhdAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileAdhdOpen, setMobileAdhdOpen] = useState(false);

  const isMobile = useMediaQuery('(max-width:1250px)');

  const zocdocLink =
    'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';

  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setAdhdAnchorEl(null);
  };

  const handleOpenAdhdMenu = (event) => setAdhdAnchorEl(event.currentTarget);
  const handleCloseAdhdMenu = () => setAdhdAnchorEl(null);

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
    if (!open) setMobileAdhdOpen(false);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setMobileAdhdOpen(false);
  };

  return (
    <>
      <Box sx={{ width: '100%', position: 'fixed', top: 0, zIndex: 1301 }}>
        <EmergencyBanner />
      </Box>

      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          background: 'linear-gradient(to right, black, #a3b287ff)',
          top: '40px',
          height: 70,
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: 70,
            height: 70,
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box',
            px: { xs: 2, sm: 3, md: 4 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            overflow: 'hidden',
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={Logo}
              alt="Aspire Behavioral Health logo"
              sx={{
                height: { xs: 38, md: 45 },
                width: 'auto',
                display: 'block',
              }}
            />
          </Box>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open navigation menu"
              onClick={toggleDrawer(true)}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexGrow: 1,
                minWidth: 0,
                ml: 3,
              }}
            >
              <Link component={RouterLink} to="/" underline="none" sx={linkStyle}>
                Home
              </Link>

              <Link
                component="button"
                underline="none"
                id="services-button"
                onClick={handleOpenMenu}
                sx={{
                  ...linkStyle,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  padding: 0,
                }}
              >
                Services
              </Link>

              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                <MenuItem onClick={handleCloseMenu} component={RouterLink} to="/psychiatry">
                  Psychiatry
                </MenuItem>

                <MenuItem onClick={handleCloseMenu} component={RouterLink} to="/telehealth">
                  Telehealth
                </MenuItem>

                <MenuItem onClick={handleOpenAdhdMenu} sx={{ fontWeight: 'bold' }}>
                  ADHD Testing
                </MenuItem>

                <MenuItem onClick={handleCloseMenu} component={RouterLink} to="/esketamine">
                  SPRAVATO® (Esketamine) Treatment
                </MenuItem>

                <MenuItem onClick={handleCloseMenu} component={RouterLink} to="/suboxone">
                  Suboxone® for Opioid Use Disorder
                </MenuItem>

                <MenuItem onClick={handleCloseMenu} component={RouterLink} to="/tms">
                  Transcranial Magnetic Stimulation (TMS)
                </MenuItem>
              </Menu>

              <Menu anchorEl={adhdAnchorEl} open={Boolean(adhdAnchorEl)} onClose={handleCloseAdhdMenu}>
                <MenuItem
                  onClick={() => {
                    handleCloseAdhdMenu();
                    handleCloseMenu();
                  }}
                  component={RouterLink}
                  to="/qbtest"
                >
                  QbTest® Assessment
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    handleCloseAdhdMenu();
                    handleCloseMenu();
                  }}
                  component={RouterLink}
                  to="/creyos"
                >
                  Creyos Assessment
                </MenuItem>
              </Menu>

              <Link component={RouterLink} to="/online-forms" underline="none" sx={linkStyle}>
                Online Forms
              </Link>

              <Link component={RouterLink} to="/about-us" underline="none" sx={linkStyle}>
                About Us
              </Link>

              <Link component={RouterLink} to="/insurance" underline="none" sx={linkStyle}>
                Insurance
              </Link>

              <Link component={RouterLink} to="/faq" underline="none" sx={linkStyle}>
                FAQ
              </Link>

              <Link
                href={zocdocLink}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={ctaStyle}
              >
                Schedule an Appointment
              </Link>
            </Box>
          )}

          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
            PaperProps={{
              sx: {
                backgroundColor: '#3b4d23',
                top: 40,
                width: 280,
              },
            }}
          >
            <Box sx={{ width: 280 }}>
              <List>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/" onClick={closeDrawer}>
                    <ListItemText primary="Home" sx={{ color: 'white' }} />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/psychiatry" onClick={closeDrawer}>
                    <ListItemText primary="Psychiatry" sx={{ color: 'white' }} />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/telehealth" onClick={closeDrawer}>
                    <ListItemText primary="Telehealth" sx={{ color: 'white' }} />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton onClick={() => setMobileAdhdOpen(!mobileAdhdOpen)}>
                    <ListItemText
                      primary="ADHD Testing"
                      sx={{
                        color: 'white',
                        '& .MuiListItemText-primary': { fontWeight: 'bold' },
                      }}
                    />
                    {mobileAdhdOpen ? (
                      <ExpandLessIcon sx={{ color: 'white' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: 'white' }} />
                    )}
                  </ListItemButton>
                </ListItem>

                <Collapse in={mobileAdhdOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItem disablePadding>
                      <ListItemButton component={RouterLink} to="/qbtest" onClick={closeDrawer} sx={{ pl: 4 }}>
                        <ListItemText primary="QbTest® Assessment" sx={{ color: 'white' }} />
                      </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                      <ListItemButton component={RouterLink} to="/creyos" onClick={closeDrawer} sx={{ pl: 4 }}>
                        <ListItemText primary="Creyos Assessment" sx={{ color: 'white' }} />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Collapse>

                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/esketamine" onClick={closeDrawer}>
                    <ListItemText primary="SPRAVATO® Treatment" sx={{ color: 'white' }} />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/suboxone" onClick={closeDrawer}>
                    <ListItemText primary="Suboxone® for Opioid Use Disorder" sx={{ color: 'white' }} />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/tms" onClick={closeDrawer}>
                    <ListItemText primary="Transcranial Magnetic Stimulation (TMS)" sx={{ color: 'white' }} />
                  </ListItemButton>
                </ListItem>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.25)', my: 1 }} />

                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/online-forms" onClick={closeDrawer}>
                    <ListItemText primary="Online Forms" sx={{ color: 'white' }} />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/about-us" onClick={closeDrawer}>
                    <ListItemText primary="About Us" sx={{ color: 'white' }} />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/insurance" onClick={closeDrawer}>
                    <ListItemText primary="Insurance" sx={{ color: 'white' }} />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/faq" onClick={closeDrawer}>
                    <ListItemText primary="FAQ" sx={{ color: 'white' }} />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton
                    component="a"
                    href={zocdocLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeDrawer}
                    sx={mobileCtaStyle}
                  >
                    <ListItemText
                      primary="Schedule an Appointment"
                      sx={{
                        color: 'white',
                        textAlign: 'center',
                        '& .MuiListItemText-primary': { fontWeight: 'bold' },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Box>
          </Drawer>
        </Toolbar>
      </AppBar>
    </>
  );
};

const linkStyle = {
  color: 'white',
  position: 'relative',
  fontSize: '0.95rem',
  fontWeight: 500,
  letterSpacing: '0.2px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  textTransform: 'none',
  '&:after': {
    content: '""',
    position: 'absolute',
    width: 0,
    height: '2px',
    bottom: -2,
    left: 0,
    bgcolor: '#a3b287ff',
    transition: 'width 0.3s ease',
  },
  '&:hover:after': { width: '100%' },
};

const ctaStyle = {
  bgcolor: '#3a4c17',
  color: 'white',
  px: 1.75,
  py: 1,
  borderRadius: '8px',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  fontSize: '0.9rem',
  flexShrink: 0,
  textTransform: 'none',
  '&:hover': {
    bgcolor: '#2e3a11',
  },
};

const mobileCtaStyle = {
  mx: 2,
  mt: 1,
  bgcolor: '#a3b287ff',
  borderRadius: '8px',
  '&:hover': { bgcolor: '#8f9f73' },
};

export default Header;